<template>
  <div class="gallery-image-list">
    <!-- Loading state -->
    <div v-if="isLoading" class="gallery-image-list__loading">
      <Wait />
    </div>

    <!-- Empty state -->
    <div v-else-if="!isLoading && imageList.length === 0" class="gallery-image-list__empty">
      <EmptyState title="No images found" description="Try adjusting your filters or upload new images" />
    </div>

    <!-- Image list -->
    <div v-else class="gallery-image-list__content">
    
      <ImageCard  v-for="imageObj in imageList"
        :key="imageObj.id"
          :imageObj="imageObj"
          :annotations="imageObj.annotations"
          :annotationSets="annotationSets"
          :annotationDisplayType="colorAnnotationsBy"
          :filterAnnotationsBySets="displayAnnotationSets"
          :filterAnnotationsByLabelIndexes="displayLabels"
          :labels="currentLabels"
          :showName="showImageName"
          :showLabels="showLabels"
          :shouldShowSelector="shouldShowSelector"
          :selected="selectedImages.some(img => img.id === imageObj.id) || selectAllFiles || selectAllPageFiles"
          v-bind="gridItemDisplayProp"
          @mousedown.stop=""
          @mouseup.stop=""
          @grid-item-clicked="$emit('grid-item-clicked', $event)"
          @update:selected="$emit('update:selected', { image: imageObj, isSelected: $event })"
        />
      
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import ImageCard from './ImageCard.vue';
import Wait from '../../utils/Wait.vue';
import EmptyState from '../../EmptyState.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import useGallery from '@/composables/annotationTool/useGallery.js';

const store = useStore();
const dataConnect = new DatastoreConnect(store.state.enterpriseServerUrl);

const {
  datasetFilters,
  combineSequenceFrames,
  sortBy,
  reverse,
} = useGallery();

const props = defineProps({
  currentPage: {
    type: Number,
    default: 1
  },
  imagesPerPage: {
    type: Number,
    default: 40
  },
  filter: {
    type: Object,
    default: null
  },
  datasetId: {
    type: [String, Number],
    required: true
  },
  annotationSets: {
    type: Array,
    default: () => []
  },
  colorAnnotationsBy: {
    type: String,
    default: null
  },
  displayAnnotationSets: {
    type: Array,
    default: () => []
  },
  displayLabels: {
    type: Array,
    default: () => []
  },
  currentLabels: {
    type: Array,
    default: () => []
  },
  showImageName: {
    type: Boolean,
    default: true
  },
  showLabels: {
    type: Boolean,
    default: true
  },
  shouldShowSelector: {
    type: Boolean,
    default: true
  },
  selectedImages: {
    type: Array,
    default: () => []
  },
  selectAllFiles: {
    type: Boolean,
    default: false
  },
  selectAllPageFiles: {
    type: Boolean,
    default: false
  },
  gridItemDisplayProp: {
    type: Object,
    default: () => ({})
  },
  imagesCount: {
    type: Number,
    default: null
  }
});

const emit = defineEmits(['grid-item-clicked', 'update:selected', 'page-image-count']);

const isLoading = ref(false);
const imageList = ref([]);

const paginatedImages = computed(() => {
  const startIndex = (props.currentPage - 1) * props.imagesPerPage;
  const endIndex = startIndex + props.imagesPerPage;
  return imageList.value.slice(startIndex, endIndex);
});

const calculateImageWidth = (imageObj) => {
  // If image has width/height info, use it to calculate proportional width
  if (imageObj.width && imageObj.height) {
    const aspectRatio = imageObj.width / imageObj.height;
    // Base height of 160px, calculate width based on aspect ratio
    const baseHeight = 160;
    const calculatedWidth = baseHeight * aspectRatio;
    return `${calculatedWidth}px`;
  }
  // Default to 140px for images without dimensions
  return '240px';
};

const fetchImages = async (offset, count, filters = {}) => {
  try {
    const params = {
      get_annotations: true,
      get_sensors: true,
      get_tags: true,
      combine_sequence_frames: combineSequenceFrames.value,
      sort_by: sortBy.value,
      reverse: reverse.value,
      offset: offset,
      limit: count,
      images_filter: { 
        dataset_id: props.datasetId,
        ...filters.images_filter,
      },
      image_files_filter: filters.image_files_filter || {},
      annotations_filter: filters.annotations_filter || null,
      image_review_status_filter: filters.image_review_status_filter || null,
      image_tags_filter: filters.image_tags_filter || null,
      sensors_filter: filters.sensors_filter || null,
    };
    
    const resp = await dataConnect.getFilteredImages(params);
    return resp?.result || [];
  } catch (error) {
    console.error('Error in fetchImages:', error);
    throw error;
  }
};

const loadImages = async () => {
  isLoading.value = true;
  try {
    const offset = (props.currentPage - 1) * props.imagesPerPage;
    
    // Build filters from datasetFilters
    const filters = {
      images_filter: {},
      image_files_filter: {},
      annotations_filter: null,
      image_review_status_filter: null,
      image_tags_filter: null,
      sensors_filter: null,
    };

    // Images filter
    if (datasetFilters.value?.groupsFilter && datasetFilters.value.groupsFilter.length > 0) {
      filters.images_filter.group_ids = datasetFilters.value.groupsFilter;
    }

    // Image files filter (includes name filter)
    if (datasetFilters.value?.imageNameFilter?.length > 0) {
      filters.image_files_filter.image_name = datasetFilters.value.imageNameFilter;
    }
    if (datasetFilters.value?.timeRangeFilter) {
      filters.image_files_filter.lower_time = datasetFilters.value.timeRangeFilter[0];
      filters.image_files_filter.upper_time = datasetFilters.value.timeRangeFilter[1];
    }
    if (datasetFilters.value?.dateRangeFilter) {
      if (datasetFilters.value.dateRangeFilter[0]) {
        filters.image_files_filter.lower_date = new Date(datasetFilters.value.dateRangeFilter[0]).toISOString();
      }
      if (datasetFilters.value.dateRangeFilter[1]) {
        filters.image_files_filter.upper_date = new Date(datasetFilters.value.dateRangeFilter[1]).toISOString();
      }
    }
    if (datasetFilters.value?.cameraSourceFilter && datasetFilters.value.cameraSourceFilter.length > 0) {
      filters.image_files_filter.image_sources = datasetFilters.value.cameraSourceFilter;
    }

    // Annotations filter
    const annotationsFilter = {};
    if (datasetFilters.value?.confidenceRangeFilter) {
      annotationsFilter.lower_score = datasetFilters.value.confidenceRangeFilter[0];
      annotationsFilter.upper_score = datasetFilters.value.confidenceRangeFilter[1];
    }
    if (datasetFilters.value?.annotationTypeFilter?.length > 0) {
      annotationsFilter.types = datasetFilters.value.annotationTypeFilter;
      if (datasetFilters.value.annotationTypeFilterInvert) {
        annotationsFilter.types_inverse = true;
      }
    }

    //MUID  somehow annotation filter is set

    // if (datasetFilters.value?.annotationSetFilter?.length > 0) {
    //   annotationsFilter.annotation_set_ids = datasetFilters.value.annotationSetFilter.map((set) => set.id);
    // }
    if (datasetFilters.value?.imagesWithAnnotationSetFilter?.length > 0) {
      annotationsFilter.images_with_annotation_set_ids = datasetFilters.value.imagesWithAnnotationSetFilter.map((set) => set.id);
    }
    if (datasetFilters.value?.annotationLabelFilter?.length > 0) {
      annotationsFilter.images_with_annotation_label_indexes = datasetFilters.value.annotationLabelFilter;
    }
    if (datasetFilters.value?.boundingBoxSizeFilter) {
      Object.entries(datasetFilters.value.boundingBoxSizeFilter).forEach(([key, value]) => {
        if (key !== 'type' && value) {
          annotationsFilter[`${key}_${datasetFilters.value.boundingBoxSizeFilter.type}`] = value;
        }
      });
    }
    filters.annotations_filter = Object.keys(annotationsFilter).length ? annotationsFilter : null;

    // Tags filter
    const imageTagFilter = {};
    if (datasetFilters.value?.imageTagFilter && datasetFilters.value.imageTagFilter.length > 0) {
      imageTagFilter.tag_names = datasetFilters.value.imageTagFilter.map((tag) => tag.name);
    }
    filters.image_tags_filter = Object.keys(imageTagFilter).length ? imageTagFilter : null;

    const imagesResult = await fetchImages(offset, props.imagesPerPage, filters);
    imageList.value = imagesResult || [];
    emit('page-image-count', imageList.value.length);
  } catch (error) {
    console.error('Error loading images:', error);
    emit('page-image-count', 0);
  } finally {
    isLoading.value = false;
  }
};

// Fetch on mount
onMounted(() => {
  loadImages();
});

// Watch for filter changes
watch(
  () => props.filter,
  () => {
    loadImages();
  },
  { deep: true }
);

// Watch for page changes
watch(
  () => props.currentPage,
  () => {
    loadImages();
  }
);

// Watch for dataset ID changes
watch(
  () => props.datasetId,
  () => {
    imageList.value = [];
    loadImages();
  }
);

// Watch for sort changes and refresh image list
watch(
  () => [sortBy.value, reverse.value],
  () => {
    loadImages();
  }
);

// Watch for image count changes (e.g., after delete/upload)
watch(
  () => props.imagesCount,
  () => {
    if (props.imagesCount !== null && props.imagesCount !== undefined) {
      loadImages();
    }
  }
);
</script>

<style lang="scss" scoped>
.gallery-image-list {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  overflow: hidden;

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 16px;
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 16px;
  }

  &__content {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 12px;
    padding: 16px;
    overflow-y: auto;
    flex: 1;
    align-content: flex-start;
  }
}

.gallery-image-item {
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  overflow: hidden;
  flex-grow: 0;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
    transform: scale(1.02);
  }
}
</style>
